import { useMemo } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  HiOutlineNewspaper,
  HiOutlineMap,
  HiOutlineFolderOpen,
  HiOutlineOfficeBuilding,
  HiOutlineLogin,
  HiOutlineUserAdd,
} from 'react-icons/hi'
import clsx from 'clsx'
import { usePermissions } from '../../hooks/usePermissions'

// Navigation items for public pages with permission keys
const navItems = [
  {
    to: '/news',
    icon: HiOutlineNewspaper,
    label: 'Tin tức',
    featureKey: 'news',
    activeColor: 'bg-blue-100 text-blue-700',
    iconActiveColor: 'text-blue-600',
    hoverColor: 'hover:bg-blue-50 hover:text-blue-600',
  },
  {
    to: '/docs',
    icon: HiOutlineFolderOpen,
    label: 'Tài liệu',
    featureKey: 'docs',
    activeColor: 'bg-amber-100 text-amber-700',
    iconActiveColor: 'text-amber-600',
    hoverColor: 'hover:bg-amber-50 hover:text-amber-600',
  },
  {
    to: '/map',
    icon: HiOutlineMap,
    label: 'Bản đồ giá',
    featureKey: 'map',
    activeColor: 'bg-emerald-100 text-emerald-700',
    iconActiveColor: 'text-emerald-600',
    hoverColor: 'hover:bg-emerald-50 hover:text-emerald-600',
  },
  {
    to: '/projects',
    icon: HiOutlineOfficeBuilding,
    label: 'Dự án',
    featureKey: 'projects',
    activeColor: 'bg-cyan-100 text-cyan-700',
    iconActiveColor: 'text-cyan-600',
    hoverColor: 'hover:bg-cyan-50 hover:text-cyan-600',
  },
]

function PublicHeader() {
  const location = useLocation()
  const { hasAccess } = usePermissions()

  // Filter nav items based on guest permissions
  const visibleNavItems = useMemo(() => {
    return navItems.filter(item => hasAccess(item.featureKey))
  }, [hasAccess])

  return (
    <header className="bg-white border-b border-secondary-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0 sm:py-6">
        <div className="flex items-center justify-between h-14">
          {/* Left: Logo + Navigation */}
          <div className="flex items-center gap-2">
            {/* Logo */}
            <Link to="/news" className="flex items-center group mr-2">
              <img
                src="/logo_dat_xanh_mien_trung.png"
                alt="Đất Xanh Miền Trung"
                className="h-8 sm:h-10 w-auto object-contain"
              />
            </Link>

            {/* Divider */}
            <div className="hidden md:block w-px h-6 bg-secondary-200 mx-1" />

            {/* Main Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {visibleNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                      isActive
                        ? item.activeColor
                        : `text-secondary-600 ${item.hoverColor}`
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className={clsx('w-4 h-4', isActive ? item.iconActiveColor : '')} />
                      <span>{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Right: Auth Buttons - luôn hiển thị cả trên mobile */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link
              to="/login"
              state={{ from: location }}
              className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-secondary-600 hover:bg-secondary-50 border border-secondary-200 transition-colors"
            >
              <HiOutlineLogin className="w-4 h-4" />
              <span>Đăng nhập</span>
            </Link>
            <Link
              to="/register"
              className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 shadow-sm transition-colors"
            >
              <HiOutlineUserAdd className="w-4 h-4" />
              <span>Đăng ký</span>
            </Link>
          </div>
        </div>
      </div>
    </header>
  )
}

export default PublicHeader
